'use client';

import classNames from 'classnames';
import Link from 'next/link';
import { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';

import { registerEventApi } from '@/entities/event/api';
import { CounterBig } from '@/features/counter';
import {
  EventFormValue,
  eventFormSchema,
} from '@/features/event/model/formSchema';
import EventFormFields from '@/features/event/ui/event-form-fields/EventFormFields';
import { useUserStore } from '@/features/user';
import {
  extractFieldErrors,
  formatEventDateRu,
  formatEventTimeRu,
  formatPrice,
  usePhoneMask,
} from '@/shared/lib';
import { Checkbox } from '@/shared/ui';
import { MODAL_TYPES } from '@/widgets/modals/config/constants';
import { useModalStore, type EventModalPayload } from '@/widgets/modals/model/store';

import styles from './eventRequestForm.module.scss';

interface EventFormProps {
  event: EventModalPayload;
  variant?: 'modal' | 'inline';
}

const EventRequestForm: FC<EventFormProps> = ({ event, variant = 'modal' }) => {
  const isInline = variant === 'inline';
  const { openModal } = useModalStore();
  const { isAuth, user } = useUserStore();
  const [form, setForm] = useState<EventFormValue>({
    name: '',
    phone: '',
    email: '',
    policy: false,
  });
  const [quantity, setQuantity] = useState(1);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof EventFormValue, string>>
  >({});
  const { phone, handlePhonePast, onChange: setPhone } = usePhoneMask();

  const eventPrice = event?.price ?? 0;
  const totalPrice = eventPrice * quantity;

  useEffect(() => {
    if (!isAuth || !user) return;

    const fullName = [user.first_name, user.last_name]
      .filter(Boolean)
      .join(' ');

    setForm((prev) => ({
      ...prev,
      name: prev.name || fullName,
      email: prev.email || user.email,
    }));

    if (!phone && user.phone) {
      handlePhonePast(user.phone);
    }
  }, [handlePhonePast, isAuth, phone, user]);

  const normalizePhone = (phoneValue: string) => {
    const digits = phoneValue.replace(/\D/g, '');
    return `+${digits}`;
  };

  const handleSubmit = async (submitEvent: FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();

    try {
      await eventFormSchema.validate({ ...form, phone }, { abortEarly: false });
      await registerEventApi(event.slug, {
        name: form.name.trim(),
        phone: normalizePhone(phone),
        email: form.email.trim(),
        count: quantity,
      });
      setFieldErrors({});
      openModal(MODAL_TYPES.eventRegistrationSuccess);
    } catch (error) {
      setFieldErrors(
        extractFieldErrors(error) as Partial<
          Record<keyof EventFormValue, string>
        >,
      );
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFieldErrors((prev) => ({ ...prev, phone: '' }));
    setPhone(event);
  };

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decreaseQuantity = () => {
    setQuantity((prev) => Math.max(1, prev - 1));
  };

  return (
    <form
      className={classNames(styles.form, isInline && styles.form_inline)}
      onSubmit={handleSubmit}
    >
      {!isInline && (
        <>
          <div className={styles.form__headBlock}>
            <p className={styles.form__head}>{'Регистрация на мероприятие'}</p>
            <p className={styles.form__head}>{event.title}</p>
          </div>

          <div className={styles.form__eventMeta}>
            <div>
              <p className={styles.form__label}>Дата и время</p>
              <p className={styles.form__value}>
                {event?.startAt
                  ? `${formatEventDateRu(event.startAt)}, ${formatEventTimeRu(event.startAt)}`
                  : '-'}
              </p>
            </div>
            <div>
              <p className={styles.form__label}>Адрес</p>
              <p className={styles.form__value}>{event?.location ?? '-'}</p>
            </div>
          </div>
        </>
      )}

      <div className={styles.form__descriptionBlock}>
        <p className={styles.form__description}>
          Для бронирования места необходимо внести 100% предоплаты
        </p>
      </div>

      <div className={styles.form__subtitleBlock}>
        <p>Личные данные</p>
      </div>

      <EventFormFields
        className={styles.form__fields}
        email={form.email}
        emailError={fieldErrors.email}
        name={form.name}
        nameError={fieldErrors.name}
        phone={phone}
        phoneError={fieldErrors.phone}
        onChange={handleInputChange}
        onPhoneChange={handlePhoneChange}
      />

      <div className={styles.form__subtitleBlock}>
        <p>Количество</p>
      </div>

      <div className={styles.form__fields}>
        <CounterBig
          className={styles.form__counter}
          count={quantity}
          decrement={decreaseQuantity}
          increment={increaseQuantity}
        />
      </div>

      <div className={styles.form__total}>
        <div className={styles.form__totalRow}>
          <p>Цена</p>
          <p>{formatPrice(eventPrice)}</p>
        </div>
        <div className={styles.form__totalRow}>
          <p>Количество</p>
          <p>{quantity}</p>
        </div>
        <div className={classNames(styles.form__totalRowBottom)}>
          <p>Итого</p>
          <p>{formatPrice(totalPrice)}</p>
        </div>
      </div>

      <button
        className={classNames(
          'button',
          isInline ? 'button--confirm-black' : 'button--confirm-white',
          styles.form__button,
          isInline && styles.form__button_inline,
        )}
      >
        Продолжить
      </button>

      <Checkbox
        checked={form.policy}
        className={styles.form__checkbox}
        error={fieldErrors.policy}
        variant={isInline ? 'grey' : 'white'}
        onChange={(value) => {
          setFieldErrors((prev) => ({ ...prev, policy: '' }));
          setForm((prev) => ({ ...prev, policy: value }));
        }}
      >
        Согласие с {''}
        <Link
          className={classNames('link link--underline')}
          href="/page/politika-konfidentsialnosti"
        >
          Политикой конфиденциальности
        </Link>
      </Checkbox>
    </form>
  );
};

export default EventRequestForm;
